import React, { useEffect } from 'react'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { useIntl } from 'react-intl'
import { CircularProgress } from '@mui/material'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import { Add } from '@mui/icons-material'
import { Box } from '@mui/system'
import CustomInput from '../../../../../components/muiComponents/customInput'
import { requiredString } from '../../../../../utils/validation'
import { videoLessonApiSlice } from '../../../../../features/section/videoLessonSlice'
import { VideoLessonState } from '../../../../../features/types'

const styles = {
    flexSpace: {
        display: 'flex',
        justifyContent: 'space-between',
    },
}

interface Props {
    courseTopicId: number
    confirmAction?: (id?: string) => void
    editVideo?: any
    editMode?: boolean
    getSections?: () => void
    setEditRow?: (element: any) => void
}

export default function AddVideoDialog({
    editVideo,
    courseTopicId,
    getSections,
    setEditRow,
}: Props) {
    const [open, setOpen] = React.useState(false)
    const { formatMessage } = useIntl()

    const [createVideoLesson, { isLoading: isCreating }] =
        videoLessonApiSlice.useCreateVideoLessonMutation()
    const [updateVideoLesson, { isLoading: isUpdating }] =
        videoLessonApiSlice.useUpdateVideoLessonMutation()

    const test: VideoLessonState = {
        id: null,
        title: '',
        link: '',
        description: '',
        duration: 0,
        isReady: true,
        position: 0,
        courseTopicId: courseTopicId,
    }

    const validationSchema = yup.object().shape({
        title: requiredString({ formatMessage }),
    })

    const formik = useFormik({
        initialValues: test,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            if (editVideo) {
                handleUpdateVideoLesson()
            } else {
                handleCreateVideoLesson()
            }
        },
    })

    const handleCreateVideoLesson = async () => {
        try {
            await createVideoLesson(formik.values).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            getSections()
            formik.resetForm()
            handleClose()
        } catch (error) {}
    }

    const handleUpdateVideoLesson = async () => {
        try {
            await updateVideoLesson(formik.values).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            getSections()
            formik.resetForm()
            handleClose()
        } catch (error) {}
    }

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setEditRow('')
        setOpen(false)
    }

    useEffect(() => {
        if (editVideo) {
            setOpen(true)
            formik.setValues(editVideo)
        } else {
            setEditRow('')
        }
    }, [editVideo])

    return (
        <div>
            <Button
                endIcon={<Add />}
                onClick={handleClickOpen}
            >
                {formatMessage({ id: 'add' })}{' '}
                {formatMessage({ id: 'videoLesson' })}
            </Button>

            <Dialog
                maxWidth={'sm'}
                fullWidth
                open={open}
                onClose={handleClose}
            >
                <DialogTitle>
                    {editVideo
                        ? formatMessage({ id: 'edit' })
                        : formatMessage({ id: 'add' })}{' '}
                    {formatMessage({ id: 'videoLesson' })}
                </DialogTitle>

                <DialogContent>
                    <form>
                        <DialogContentText>
                            {formatMessage({ id: 'name' })}
                        </DialogContentText>

                        <CustomInput
                            id="title"
                            name="title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            helperText={formik.errors.title}
                            error={Boolean(formik.errors.title)}
                            fullWidth
                        />

                        <DialogContentText>
                            {formatMessage({ id: 'link' })}
                        </DialogContentText>

                        <CustomInput
                            id="link"
                            name="link"
                            value={formik.values.link}
                            onChange={formik.handleChange}
                            fullWidth
                        />

                        <DialogContentText>
                            {formatMessage({ id: 'description' })}
                        </DialogContentText>

                        <CustomInput
                            id="description"
                            name="description"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            fullWidth
                            multiline
                            maxRows={10}
                            minRows={10}
                        />
                        <Box sx={styles.flexSpace}>
                            <Box>
                                <DialogContentText>
                                    {formatMessage({ id: '_duration' })}(сек)
                                </DialogContentText>

                                <CustomInput
                                    id="duration"
                                    name="duration"
                                    value={formik.values.duration}
                                    onChange={formik.handleChange}
                                    fullWidth
                                />
                            </Box>
                            <Box>
                                <DialogContentText>
                                    {formatMessage({ id: 'position' })}
                                </DialogContentText>

                                <CustomInput
                                    id="position"
                                    name="position"
                                    value={formik.values.position}
                                    onChange={formik.handleChange}
                                    fullWidth
                                />
                            </Box>
                        </Box>
                        {/* <FormControlLabel
                            control={
                                <Checkbox
                                    name="isReady"
                                    checked={formik?.values?.isReady}
                                    onChange={formik.handleChange}
                                />
                            }
                            label={formatMessage({ id: 'ready' }).concat(' ?')}
                        /> */}
                    </form>
                </DialogContent>

                <DialogActions sx={{ m: 2 }}>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => formik.handleSubmit()}
                        disabled={isCreating || isUpdating}
                    >
                        {isCreating || isUpdating ? (
                            <CircularProgress
                                size={20}
                                sx={{ mr: 1 }}
                            />
                        ) : editVideo ? (
                            formatMessage({ id: 'edit' })
                        ) : (
                            formatMessage({ id: 'add' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
